import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Alert, AlertTitle, Box, Button, Card, CardActions, CardContent, Chip, Divider, Grid, IconButton, ListItemButton, MenuItem, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import SelectV2 from "../../Common/Form/SelectV2";
import { PieChart } from '@mui/x-charts/PieChart';
import { useAppSelector } from "../../Common/_hooks/useAppSelector";
import { useAppDispatch } from "../../Common/_hooks/useAppDispatch";
import { Link, useParams } from "react-router";
import LabLoader from "../../DesignLab/_components/LabLoader";
import Dinero from "dinero.js";
import { CompleteStoreRating, FetchStoreOverview, type StoreOverviewPayload } from "../../UserData/_actions/Store/StoreActions";
import StoreHeader from "./StoreHeader";
import { DateTime } from "luxon";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import CardButton from "../../Common/Buttons/CardButton";

type Period = 'today' | 'last7' | 'last30'
interface ChartLegendItem {
  id: number
  value: number
  label: string
  color: string
}
type ActionRequiredOrder = StoreOverviewPayload['actionRequiredOrders'][number]
type LastProductSale = StoreOverviewPayload['lastProductSales'][number]

const CHART_HEIGHT = 164
const ACTION_REQUIRED_DISPLAY_LIMIT = 2
const PIE_SERIES_PROPS = {
  innerRadius: 28,
  outerRadius: 58,
  paddingAngle: 2,
  cornerRadius: 4,
}
const PRODUCT_TYPE_COLORS = ['#14b8b1', '#f57c00', '#78c9e7', '#2e7d32', '#8b5cf6', '#6b7280']
const SHOPIFY_REVIEW_URL = 'https://apps.shopify.com/art-of-where/reviews'
const GRID_CARD_ITEM_SX = { display: 'flex' }
const CARD_FILL_SX = { width: '100%', height: '100%' }
const CARD_CONTENT_FLUSH_SX = { height: '100%', p: 0, '&:last-child': { pb: 0 } }
const CARD_SECTION_HEADER_SX = { px: 3, pt: 3, pb: 1 }

const DEFAULT_OVERVIEW: StoreOverviewPayload = {
  summary: {
    orders: 0,
    productsSold: 0,
    cost: 0,
    profit: 0,
  },
  ordersOverview: {
    awaitingPayment: 0,
    paymentProcessed: 0,
    inProduction: 0,
    shipped: 0,
  },
  actionRequiredOrders: [],
  productTypeSales: [],
  lastProductSales: [],
  showRatingBadge: false,
}

export default function StoreOverview() {
  const dispatch = useAppDispatch()
  const [t] = useTranslation('account-v2')
  const [tR] = useTranslation('routes')
  const params = useParams<{ storeSlug: string }>()

  const currency = useAppSelector(state => state.get('userData').get('prefs').get('currency'))
  const loader = useAppSelector(state => state.get('UIData').get('loaders').get('fetch_store_overview'))

  const [period, setPeriod] = useState<Period>('last30')
  const [overview, setOverview] = useState<StoreOverviewPayload>(DEFAULT_OVERVIEW)
  const [showRatingBadge, setShowRatingBadge] = useState(false)

  const onChangePeriod = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setPeriod(e.target.value as Period)
  }, [])

  useEffect(() => {
    if (!params.storeSlug) return

    dispatch(FetchStoreOverview(params.storeSlug, period).set({
      onSuccess: (response) => {
        setOverview(response.data)
        setShowRatingBadge(response.data.showRatingBadge)
      }
    }))
  }, [dispatch, params.storeSlug, period])

  const snoozeRatingBadge = useCallback(() => {
    const expires = new Date()
    expires.setDate(expires.getDate() + 14)
    document.cookie = 'dashboard-snooze-rating=1; expires=' + expires.toUTCString() + '; path=/; SameSite=Lax'
    setShowRatingBadge(false)
  }, [])

  const onLeaveRating = useCallback(() => {
    if (!params.storeSlug) return

    dispatch(CompleteStoreRating(params.storeSlug, true))
    setShowRatingBadge(false)
  }, [dispatch, params.storeSlug])

  const onDeclineRating = useCallback(() => {
    if (!params.storeSlug) return

    dispatch(CompleteStoreRating(params.storeSlug, false))
    setShowRatingBadge(false)
  }, [dispatch, params.storeSlug])

  const formatMoney = useCallback((value: number) => {
    return Dinero({
      amount: Math.round(value * 100),
      currency,
    }).toFormat()
  }, [currency])

  const pieData = useMemo(() => {
    return overview.productTypeSales.map((productType, index) => ({
      id: index,
      value: productType.value,
      label: productType.label,
      color: PRODUCT_TYPE_COLORS[index % PRODUCT_TYPE_COLORS.length],
    }))
  }, [overview.productTypeSales])

  const orderTrackingPieData = useMemo(() => {
    return [
      { id: 0, value: overview.ordersOverview.awaitingPayment, label: t('Awaiting payment'), color: '#f57c00' },
      { id: 1, value: overview.ordersOverview.paymentProcessed, label: t('Payment processed'), color: '#78c9e7' },
      { id: 2, value: overview.ordersOverview.inProduction, label: t('In production'), color: '#00aced' },
      { id: 3, value: overview.ordersOverview.shipped, label: t('Shipped'), color: '#2e7d32' },
    ]
  }, [overview.ordersOverview, t])

  const hasProductTypeSales = useMemo(() => {
    return pieData.some(item => item.value > 0)
  }, [pieData])

  const hasCurrentOrders = useMemo(() => {
    return orderTrackingPieData.some(item => item.value > 0)
  }, [orderTrackingPieData])

  const marginPercent = useMemo(() => {
    const revenue = overview.summary.cost + overview.summary.profit
    if (revenue === 0) return 0

    return (overview.summary.profit / revenue) * 100
  }, [overview.summary.cost, overview.summary.profit])

  const actionRequiredCount = useMemo(() => {
    return overview.actionRequiredOrders.length
  }, [overview.actionRequiredOrders])

  const visibleActionRequiredOrders = useMemo(() => {
    return overview.actionRequiredOrders.slice(0, ACTION_REQUIRED_DISPLAY_LIMIT)
  }, [overview.actionRequiredOrders])

  const hasMoreActionRequiredOrders = actionRequiredCount > ACTION_REQUIRED_DISPLAY_LIMIT
  const storeSlug = params.storeSlug ?? ''
  const actionRequiredOrdersUrl = tR('/account/stores/{{store}}/orders', { store: storeSlug }) + '?statuses=ordered,on-hold'

  const getOrderStatusLabel = useCallback((status: 'ordered' | 'on-hold') => {
    switch (status) {
      case 'on-hold': return t('On hold')
      default: return t('Awaiting payment')
    }
  }, [t])

  const formatOrderDate = useCallback((date: string | null) => {
    if (!date) return ''

    return DateTime.fromISO(date).toLocaleString(DateTime.DATE_MED)
  }, [])

  const renderOrderThumbnail = useCallback((thumbnail: string, index: number) => {
    return <Box
      key={thumbnail + index}
      component="span"
      sx={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        border: '2px solid #fff',
        bgcolor: 'grey.100',
      }}
    >
      <Box
        component="img"
        src={thumbnail}
        alt=""
        sx={{
          height: '100%',
          display: 'block',
        }}
      />
    </Box>
  }, [])

  const renderChartLegendItem = useCallback((item: ChartLegendItem) => {
    return <Stack key={item.id} direction="row" spacing={1} alignItems="center">
      <Box
        component="span"
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: item.color,
          flexShrink: 0,
        }}
      />
      <Typography variant="body1" sx={{ flexGrow: 1 }}>{item.label}</Typography>
      <Typography variant="body1" color="text.secondary">{item.value}</Typography>
    </Stack>
  }, [])

  const renderActionRequiredOrder = useCallback((order: ActionRequiredOrder) => {
    const thumbnails = order.thumbnails

    return <ListItemButton
      key={order.id}
      component={Link}
      to={tR('/account/stores/{{store}}/orders/{{ref_id}}', { store: storeSlug, ref_id: order.refId })}
      sx={{ px: 3, py: 1.5 }}
    >
      <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
        <Box component="div" sx={{ width: 96, flexShrink: 0 }}>
          <Typography variant="h4">#{order.refId.toUpperCase()}</Typography>
          <Typography variant="caption" color="text.secondary">{formatOrderDate(order.date)}</Typography>
        </Box>
        <Box component="div" sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="body1" noWrap>{order.name || order.country}</Typography>
          {thumbnails.length > 0 ? <Stack direction="row" spacing={-1} sx={{ mt: 0.5 }}>
            {thumbnails.map(renderOrderThumbnail)}
          </Stack> : <Typography variant="body2" color="text.secondary">{t('{{count}} products', { count: order.productsSold })}</Typography>}
        </Box>
        <Stack spacing={0.75} alignItems="flex-end" sx={{ flexShrink: 0 }}>
          <Chip
            variant="square-filled"
            size="small"
            color="warning"
            label={getOrderStatusLabel(order.status)}
          />
          <Typography variant="body2">{formatMoney(order.total)}</Typography>
        </Stack>
      </Stack>
    </ListItemButton>
  }, [formatMoney, formatOrderDate, getOrderStatusLabel, renderOrderThumbnail, storeSlug, t, tR])

  const renderLastProductSale = useCallback((product: LastProductSale, index: number) => {
    return <ListItemButton
      key={product.title + index}
      component={Link}
      to={tR('/account/stores/{{store}}/orders/{{ref_id}}', { store: storeSlug, ref_id: product.refId })}
      sx={{ px: 3, py: 1 }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
        <Box
          component="img"
          src={product.preview}
          alt={product.title}
          sx={{
            width: 48,
            height: 48,
            objectFit: 'cover',
            borderRadius: 1.5,
            flexShrink: 0,
          }}
        />
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography variant="body1" fontWeight={600} noWrap>{product.title}</Typography>
          <Typography variant="body2" color="text.secondary" noWrap>{product.location}</Typography>
        </Stack>
      </Stack>
    </ListItemButton>
  }, [storeSlug, tR])

  return <div>
    <StoreHeader
      title={t('Overview')}
      actions={<Stack direction="row" spacing={2}>
        <SelectV2
          value={period}
          onChange={onChangePeriod}
          disabled={!!loader}
        >
          <MenuItem value="today">{t('Today')}</MenuItem>
          <MenuItem value="last7">{t('Last 7 days')}</MenuItem>
          <MenuItem value="last30">{t('Last 30 days')}</MenuItem>
        </SelectV2>
      </Stack>}
    />

    {loader ? <LabLoader /> : <Stack spacing={4}>
      {showRatingBadge ? <Alert
        severity="info"
        variant="outlined"
        sx={{
          position: 'relative',
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
      >
        <Box
          component="div"
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: 'row',
            },
            alignItems: {
              xs: 'flex-start',
              sm: 'flex-start',
            },
            gap: {
              xs: 1.5,
              sm: 2,
            },
            pr: {
              xs: 0,
              sm: 4,
            },
          }}
        >
          <Box
            component="div"
            sx={{
              flexGrow: 1,
              minWidth: 0,
              pr: {
                xs: 4,
                sm: 0,
              },
            }}
          >
            <AlertTitle>{t('Enjoying Art of Where on Shopify?')}</AlertTitle>
            {t('If the integration has been helping with your orders, a Shopify review helps other artists find us too.')}
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              flexShrink: 0,
              flexWrap: 'wrap',
              rowGap: 1,
              mt: -0.75,
            }}
          >
            <Button
              component="a"
              href={SHOPIFY_REVIEW_URL}
              target="_blank"
              rel="noreferrer"
              size="small"
              color="inherit"
              endIcon={<OpenInNewRoundedIcon fontSize="small" />}
              onClick={onLeaveRating}
            >{t('Leave a review')}</Button>
            <Button
              size="small"
              color="inherit"
              onClick={onDeclineRating}
            >{t('No thanks')}</Button>
          </Stack>
          <IconButton
            size="small"
            color="inherit"
            aria-label={t('Remind me later')}
            onClick={snoozeRatingBadge}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
            }}
          ><CloseRoundedIcon fontSize="small" /></IconButton>
        </Box>
      </Alert> : null}

      <div>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4} lg={4} sx={GRID_CARD_ITEM_SX}>
            <Card elevation={0} sx={CARD_FILL_SX}>
              <CardContent sx={{ height: '100%' }}>
                <Stack spacing={1}>
                  <Typography variant="h3" sx={{ color: '#666' }}>{t('Orders confirmed')}</Typography>
                  <Typography variant="h2" component="p">{overview.summary.orders}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('{{count}} products sold', { count: overview.summary.productsSold })}</Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4} lg={4} sx={GRID_CARD_ITEM_SX}>
            <Card elevation={0} sx={CARD_FILL_SX}>
              <CardContent sx={{ height: '100%' }}>
                <Stack spacing={1}>
                  <Typography variant="h3" sx={{ color: '#666' }}>{t('Cost')}</Typography>
                  <Typography variant="h2" component="p">{formatMoney(overview.summary.cost)}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('Before taxes and shipping')}</Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4} lg={4} sx={GRID_CARD_ITEM_SX}>
            <Card elevation={0} sx={CARD_FILL_SX}>
              <CardContent sx={{ height: '100%' }}>
                <Stack spacing={1}>
                  <Typography variant="h3" sx={{ color: '#666' }}>{t('Profit')}</Typography>
                  <Typography variant="h2" component="p">{formatMoney(overview.summary.profit)}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('{{percent}} margin', { percent: marginPercent.toFixed(1) + '%' })}</Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={6} sx={GRID_CARD_ITEM_SX}>
            <Card elevation={0} sx={CARD_FILL_SX}>
              <CardContent sx={{ height: '100%' }}>
                <Stack spacing={2}>
                  <Typography variant="h3" sx={{ color: '#666' }}>{t('Order tracking')}</Typography>
                  {hasCurrentOrders ? <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      component="div"
                      sx={{
                        flexGrow: 0,
                      }}
                    >
                      <PieChart
                        series={[
                          {
                            ...PIE_SERIES_PROPS,
                            data: orderTrackingPieData,
                          },
                        ]}
                        width={CHART_HEIGHT}
                        height={CHART_HEIGHT}
                        margin={{ top: 4, bottom: 4, left: 4, right: 4 }}
                        slotProps={{
                          legend: {
                            hidden: true,
                          },

                        }}
                      />
                    </Box>
                    <Stack spacing={1} sx={{ flexGrow: 1 }}>
                      {orderTrackingPieData.map(renderChartLegendItem)}
                    </Stack>
                  </Stack> : <Alert severity="info">{t('There are no current store orders to track right now.')}</Alert>}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={6} sx={GRID_CARD_ITEM_SX}>
            <Card elevation={0} sx={{ ...CARD_FILL_SX, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ ...CARD_CONTENT_FLUSH_SX, flexGrow: 1 }}>
                <Stack spacing={0}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={CARD_SECTION_HEADER_SX}>
                    <Typography variant="h3" sx={{ color: '#666' }}>{t('Needs action')}</Typography>
                    {actionRequiredCount > 0 ? <Chip
                      color="warning"
                      size="small"
                      label={actionRequiredCount}
                      sx={{
                        borderRadius: '50%',
                        minWidth: 22,
                        height: 22,
                        '& .MuiChip-label': {
                          px: 0,
                        },
                      }}
                    /> : null}
                  </Stack>
                  {overview.actionRequiredOrders.length > 0 ? <Stack divider={<Divider />}>
                    {visibleActionRequiredOrders.map(renderActionRequiredOrder)}
                  </Stack> : <Box component="div" sx={{ px: 3, pb: 3 }}><Alert severity="success">{t('No store orders need action right now.')}</Alert></Box>}
                </Stack>
              </CardContent>
              {hasMoreActionRequiredOrders ? <CardActions sx={{ justifyContent: 'flex-end', background: 'transparent' }}>
                <CardButton
                  component={Link}
                  to={actionRequiredOrdersUrl}
                  color="primary"
                >{t('See more')}</CardButton>
              </CardActions> : null}
            </Card>
          </Grid>

          <Grid item xs={12} lg={6} sx={GRID_CARD_ITEM_SX}>
            <Card elevation={0} sx={CARD_FILL_SX}>
              <CardContent sx={CARD_CONTENT_FLUSH_SX}>
                <Stack spacing={0}>
                  <Typography variant="h3" sx={{ color: '#666', ...CARD_SECTION_HEADER_SX }}>{t('Last product sales')}</Typography>
                  {overview.lastProductSales.length > 0 ? <Stack divider={<Divider />}>
                    {overview.lastProductSales.map(renderLastProductSale)}
                  </Stack> : <Box component="div" sx={{ px: 3, pb: 3 }}><Alert severity="info">{t('There are no product sales in the selected period.')}</Alert></Box>}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={6} sx={GRID_CARD_ITEM_SX}>
            <Card elevation={0} sx={CARD_FILL_SX}>
              <CardContent sx={{ height: '100%' }}>
                <Stack spacing={2}>
                  <Typography variant="h3" sx={{ color: '#666' }}>{t('Product types sold')}</Typography>
                  {hasProductTypeSales ? <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      component="div"
                      sx={{
                        flexGrow: 0,
                      }}
                    >
                      <PieChart
                        series={[
                          {
                            ...PIE_SERIES_PROPS,
                            data: pieData,
                          },
                        ]}
                        width={CHART_HEIGHT}
                        height={CHART_HEIGHT}
                        margin={{ top: 4, bottom: 4, left: 4, right: 4 }}
                        slotProps={{
                          legend: {
                            hidden: true,
                          },
                        }}
                      />
                    </Box>
                    <Stack spacing={1} sx={{ flexGrow: 1 }}>
                      {pieData.map(renderChartLegendItem)}
                    </Stack>
                  </Stack> : <Alert severity="info">{t('There are no product sales in the selected period.')}</Alert>}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </div>
    </Stack>}
  </div>
}
